import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { ArrowRight, Shield, Globe, CheckCircle, Users, Sparkles } from 'lucide-react';

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
};

const fadeInLeft = {
  initial: { opacity: 0, x: -30 },
  animate: { opacity: 1, x: 0 },
};

const fadeInRight = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0 },
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
};

export const HeroSection = ({ stats }) => {
  const { t } = useTranslation();
  
  const trustBadges = [
    { icon: Shield, text: t('common.securePayments', 'Secure Payments') },
    { icon: CheckCircle, text: t('common.buyerProtection', 'Buyer Protection') },
    { icon: Globe, text: t('common.qualityAssured', 'Quality Assured') },
    { icon: Users, text: t('common.users', '10,000+ Users') }
  ];

  return (
    <section 
      className="relative min-h-[90vh] flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Premium Background with Overlay */}
      <div className="absolute inset-0">
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/bb052137-473a-4b30-a3c1-0ea12ef31e4c/images/67ad4f32b7ab30782777507c2c5bbbbf94a0e18a7159bae25b77ad1a0ce8964e.png"
          alt="African port trade"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/90 via-[#0A0A0A]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/50 via-transparent to-transparent" />
      </div>

      {/* Animated Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(#C25E30 1px, transparent 1px), linear-gradient(90deg, #C25E30 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }} />
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial="initial"
            animate="animate"
            transition={{ staggerChildren: 0.1 }}
            className="space-y-8"
          >
            {/* Badge */}
            <motion.div 
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md text-white px-5 py-2.5 rounded-full text-sm font-semibold border border-white/20"
            >
              <Sparkles className="w-4 h-4 text-[#C25E30]" />
              <span>{t('hero.badge', "Africa's #1 B2B Trade Platform")}</span>
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            </motion.div>
            
            {/* Main Heading */}
            <motion.h1 
              variants={fadeInUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-[1.05] tracking-tight"
            >
              Connect with
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#C25E30] to-[#E8A54B]">
                Africa's Finest
              </span>
              <br />
              <span className="text-white/90">Manufacturers</span>
            </motion.h1>
            
            {/* Subtitle */}
            <motion.p 
              variants={fadeInUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg sm:text-xl text-white/70 max-w-xl leading-relaxed"
            >
              {t('hero.subtitle', 'Source quality products directly from verified African suppliers. Agricultural goods, minerals, textiles & more from 54 countries.')}
            </motion.p>
            
            {/* CTA Buttons */}
            <motion.div 
              variants={fadeInUp}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-4 pt-4"
            >
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-[#C25E30] hover:bg-[#A64D24] text-white btn-premium gap-3 shadow-lg shadow-[#C25E30]/30 px-8 py-6 text-base font-semibold rounded-xl" 
                  data-testid="hero-get-started-btn"
                >
                  {t('hero.cta', 'Start Sourcing')} 
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/become-a-supplier">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white/30 text-white hover:bg-white/10 backdrop-blur-sm px-8 py-6 text-base font-semibold rounded-xl transition-all" 
                  data-testid="hero-supplier-btn"
                >
                  {t('hero.secondaryCta', 'Become a Supplier')}
                </Button>
              </Link>
            </motion.div>

            {/* Trust Badges */}
            <motion.div 
              variants={fadeInUp}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap gap-6 pt-6"
            >
              {trustBadges.map((badge, idx) => (
                <div 
                  key={badge.text}
                  className="flex items-center gap-2 text-sm text-white/60"
                >
                  <badge.icon className="w-4 h-4 text-[#C25E30]" />
                  <span>{badge.text}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Side - Stats Cards */}
          <motion.div
            initial="initial"
            animate="animate"
            transition={{ staggerChildren: 0.15, delayChildren: 0.3 }}
            className="hidden lg:grid grid-cols-2 gap-5"
          >
            {/* Main Stats Card */}
            <motion.div 
              variants={scaleIn}
              transition={{ duration: 0.5 }}
              className="col-span-2 glass rounded-2xl p-6 border border-white/20"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/90 text-sm mb-1">Trade Volume</p>
                  <p className="text-4xl font-bold text-white">$2.5B+</p>
                </div>
                <div className="w-16 h-16 bg-[#C25E30]/20 rounded-2xl flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-8 h-8 text-[#C25E30]" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 6l-9.5 9.5-5-5L1 18"/>
                    <path d="M17 6h6v6"/>
                  </svg>
                </div>
              </div>
            </motion.div>
            
            {/* Secondary Stats */}
            <motion.div 
              variants={scaleIn}
              transition={{ duration: 0.5 }}
              className="glass rounded-2xl p-5 border border-white/20"
            >
              <div className="w-10 h-10 bg-[#1E3A8A]/20 rounded-xl flex items-center justify-center mb-3">
                <Users className="w-5 h-5 text-[#1E3A8A]" />
              </div>
              <p className="text-2xl font-bold text-white">{stats?.total_suppliers || '40'}+</p>
              <p className="text-white/90 text-sm">Verified Suppliers</p>
            </motion.div>
            
            <motion.div 
              variants={scaleIn}
              transition={{ duration: 0.5 }}
              className="glass rounded-2xl p-5 border border-white/20"
            >
              <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center mb-3">
                <Globe className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-2xl font-bold text-white">54</p>
              <p className="text-white/90 text-sm">African Countries</p>
            </motion.div>
            
            <motion.div 
              variants={scaleIn}
              transition={{ duration: 0.5 }}
              className="glass rounded-2xl p-5 border border-white/20"
            >
              <div className="w-10 h-10 bg-[#C25E30]/20 rounded-xl flex items-center justify-center mb-3">
                <Shield className="w-5 h-5 text-[#C25E30]" />
              </div>
              <p className="text-2xl font-bold text-white">{stats?.total_products || '150'}+</p>
              <p className="text-white/90 text-sm">Products Listed</p>
            </motion.div>
            
            <motion.div 
              variants={scaleIn}
              transition={{ duration: 0.5 }}
              className="glass rounded-2xl p-5 border border-white/20"
            >
              <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center mb-3">
                <CheckCircle className="w-5 h-5 text-purple-400" />
              </div>
              <p className="text-2xl font-bold text-white">4%</p>
              <p className="text-white/90 text-sm">Low Commission</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#FAFAF9] to-transparent" />
    </section>
  );
};
